import { ColumnVisibilityModal } from "../../../../datagrid/ColumnVisibilityModal";
import { DiscoverGameAnalysisDialog } from "../../../DiscoverGameAnalysisDialog";
import { DiscoverGamesBulkUpdateModal } from "../DiscoverGamesBulkUpdateModal";
import { DiscoverGamesProgramGameModal } from "../programGameModal";
import type { useDiscoverGamesBatchActions } from "../hooks/useDiscoverGamesBatchActions";
import type { useDiscoverGamesColumnConfig } from "../hooks/useDiscoverGamesColumnConfig";
import type { useDiscoverGamesProgramGameModal } from "../hooks/useDiscoverGamesProgramGameModal";
import type { useDiscoverGamesViewState } from "../hooks/useDiscoverGamesViewState";
import client from "libs/client";
import { parseMessageAttribute } from "libs/utils";
import { _ } from "i18n";
import Modal from "ui-kit/src/Modal/Modal";

type View = ReturnType<typeof useDiscoverGamesViewState>;
type ColConfig = ReturnType<typeof useDiscoverGamesColumnConfig>;
type Batch = ReturnType<typeof useDiscoverGamesBatchActions>;
type ProgramGameModal = ReturnType<typeof useDiscoverGamesProgramGameModal>;
type GameAnalysisModal = {
	gameId: number | null;
	close: () => void;
};

interface DiscoverGamesTableModalsProps {
	view: View;
	colConfig: ColConfig;
	batch: Batch;
	programGameModal: ProgramGameModal;
	gameAnalysisModal: GameAnalysisModal;
}

export function DiscoverGamesTableModals({
	view,
	colConfig,
	batch,
	programGameModal,
	gameAnalysisModal,
}: DiscoverGamesTableModalsProps) {
	const shouldFetchGameAnalysisGame = gameAnalysisModal.gameId !== null;
	const gameAnalysisGameQuery = client.getDiscoverGameV2.useQuery(
		{ id: gameAnalysisModal.gameId ?? 0 },
		{
			enabled: shouldFetchGameAnalysisGame,
			queryKey: ["discoverGamesV2AnalysisDialog", gameAnalysisModal.gameId],
		},
	);
	const gameAnalysisGame = gameAnalysisGameQuery.data?.data;
	const gameAnalysisErrorMessage = gameAnalysisGameQuery.isError
		? parseMessageAttribute(gameAnalysisGameQuery.error) || _("app.coach.error.timeout")
		: null;

	return (
		<>
			{view.showColumnModal ? (
				<ColumnVisibilityModal
					columnGroups={view.columnGroups}
					columnVisibility={view.visibleColumns}
					defaultVisibility={view.defaultVisibility}
					onVisibilityChange={view.handleVisibilityChange}
					onClose={() => view.setShowColumnModal(false)}
					columnOrder={view.columnOrder}
					defaultColumnOrder={view.defaultColumnOrder}
					customViews={view.customViews}
					selectedCustomViewId={view.selectedCustomViewId}
					onCustomViewsChange={view.handleCustomViewsChange}
					groupColors={view.groupColors}
				/>
			) : null}

			<DiscoverGamesBulkUpdateModal
				isOpen={batch.showBulkModal}
				onClose={() => batch.setShowBulkModal(false)}
				selectedIds={batch.selectedIds}
				selectedCount={batch.selectedCount}
				clubManagerOptions={colConfig.clubMetadataDropdownOptions}
				contentManagerOptions={colConfig.clubMetadataDropdownOptions}
				assigneeOptions={colConfig.assigneeDropdownOptions}
				gameStatusOptions={colConfig.gameStatusRowOptions}
				isSubmitting={batch.isBulkApplying}
				eligibility={batch.eligibility}
				onApply={batch.handleBulkApply}
				canEditGameUploads={colConfig.canEditGameUploads}
				canEditAssignee={colConfig.canEditAssignee}
				canEditGameStatus={colConfig.canEditGameStatus}
				canEditPriority={colConfig.canEditPriority}
			/>

			<DiscoverGamesProgramGameModal
				isOpen={programGameModal.isOpen}
				onClose={programGameModal.close}
				ownershipOptions={programGameModal.ownershipOptions}
			/>

			<Modal
				show={gameAnalysisModal.gameId !== null}
				onClose={gameAnalysisModal.close}
				title={`Game Analysis for game #${gameAnalysisModal.gameId ?? ""}`}
				size="lg"
			>
				{gameAnalysisGame != null ? (
					<DiscoverGameAnalysisDialog
						gameData={gameAnalysisGame}
						invalidationQueryKey={["getDiscoverGamesDynamic", "discoverGamesV2AnalysisDialog"]}
					/>
				) : gameAnalysisGameQuery.isLoading || gameAnalysisGameQuery.isFetching ? (
					<div>{_("datatables.processing")}</div>
				) : gameAnalysisErrorMessage !== null ? (
					<div className="form-error" role="alert">{gameAnalysisErrorMessage}</div>
				) : (
					<div>{_("datatables.empty_table")}</div>
				)}
			</Modal>
		</>
	);
}
